// Fuel mechanic (design doc 16.50, LOCKED interview pass). Gates Fast
// Travel and Warp only -- nothing else (Move To, orbiting, scanning,
// combat) touches this pool (Section 5, superseded by 16.50 to cover
// both travel actions instead of "warp only").
import type { NpcHullTierLabel } from "./types";
import { devTuning } from "./devTuning";

// Consumption rate scales by hull size, linearly by tier: each tier
// doubles the previous one's rate (16.50).
export const FUEL_RATE_MULTIPLIER: Record<NpcHullTierLabel, number> = {
  Small: 1,
  Medium: 2,
  Large: 4,
  Capital: 8
};

// A Warp costs 5x what a Fast Travel costs, for the same hull (16.50).
const WARP_COST_MULTIPLIER = 5;

// Small hull's own Fast Travel cost is the doc's illustrative base unit
// (16.50: "let a Small hull's Fast Travel cost = 10 fuel") -- dev-tunable
// (16.65) via devTuning.fuelBaseCost, default 10.
export function fastTravelFuelCost(hullTierLabel: NpcHullTierLabel): number {
  return devTuning.fuelBaseCost * FUEL_RATE_MULTIPLIER[hullTierLabel];
}

export function warpFuelCost(hullTierLabel: NpcHullTierLabel): number {
  return fastTravelFuelCost(hullTierLabel) * WARP_COST_MULTIPLIER;
}

// Starting/tank capacity. 16.50 locked only the Small hull's own number
// (10 Fast Travels + 5 Warps = 350 fuel at the default base cost) and
// explicitly left open whether Medium/Large/Capital's capacity scales by
// the same rate multiplier as consumption, or independently. 16.65
// resolves this: capacity scales FASTER than consumption, per Section
// 11's locked "large ship = large capacities → long range, long
// endurance" principle -- a bigger hull should get more total jumps out
// of a full tank, not just the same jump count at a bigger absolute
// number. `capacityMultiplier = rateMultiplier ^ exponent` (dev-tunable,
// default 1.3); at exponent 1.0 this degrades exactly to the old
// same-endurance behavior.
export function startingFuelCapacity(hullTierLabel: NpcHullTierLabel): number {
  const rateMultiplier = FUEL_RATE_MULTIPLIER[hullTierLabel];
  const capacityMultiplier = Math.pow(rateMultiplier, devTuning.fuelCapacityScalingExponent);
  const smallCapacity = 10 * fastTravelFuelCost("Small") + 5 * warpFuelCost("Small");
  return smallCapacity * capacityMultiplier;
}
